import React, { useMemo, useState } from 'react'
import { Box } from '@mui/material'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import BaseModal, { type BaseModalFooterAction } from '@/components/common/base/BaseModal'
import { FORM_MODAL_VIEWPORT_MAX_HEIGHT } from '@/components/common/styles/formModalLayout'
import { useI18n } from '@/contexts/I18nContext'
import { KiotListExpandModalContext } from './kiotListExpandModalContext'

const MODAL_WIDTH = 'min(1680px, 98vw)'

export type KiotListExpandDetailModalProps = {
  open: boolean
  onClose: () => void
  title: React.ReactNode
  /** Có → nút «Chỉnh sửa» ở footer. */
  onEdit?: () => void
  children: React.ReactNode
}

/**
 * Modal xem chi tiết dùng lại panel mở rộng dòng (`KiotListExpandDetailTabs`):
 * nút hành động của panel được đưa xuống footer BaseModal, cạnh «Đóng» / «Chỉnh sửa».
 */
const KiotListExpandDetailModal: React.FC<KiotListExpandDetailModalProps> = ({
  open,
  onClose,
  title,
  onEdit,
  children,
}) => {
  const { common: commonT } = useI18n()
  const [footerHost, setFooterHost] = useState<HTMLElement | null>(null)
  const [editLocked, setEditLocked] = useState(false)
  const ctxValue = useMemo(() => ({ footerHost, setEditLocked }), [footerHost])

  const footerActions: BaseModalFooterAction[] = [
    {
      id: 'close',
      label: commonT('actions.close'),
      onClick: onClose,
      variant: 'outlined',
      startIcon: <CloseOutlinedIcon sx={{ fontSize: 18 }} />,
    },
    ...(onEdit && !editLocked
      ? [
          {
            id: 'edit',
            label: commonT('actions.edit'),
            onClick: onEdit,
            variant: 'contained' as const,
            startIcon: <EditOutlinedIcon sx={{ fontSize: 18 }} />,
          },
        ]
      : []),
  ]

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={title}
      maxWidth={false}
      width={MODAL_WIDTH}
      headerIntent="view"
      closeButtonAriaLabel={commonT('actions.close')}
      paperSx={{
        width: MODAL_WIDTH,
        maxWidth: MODAL_WIDTH,
        height: `min(1000px, ${FORM_MODAL_VIEWPORT_MAX_HEIGHT})`,
        maxHeight: FORM_MODAL_VIEWPORT_MAX_HEIGHT,
      }}
      contentSx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
      footerStart={
        <Box
          ref={setFooterHost}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 1,
          }}
        />
      }
      footerActions={footerActions}
    >
      <KiotListExpandModalContext.Provider value={ctxValue}>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          {children}
        </Box>
      </KiotListExpandModalContext.Provider>
    </BaseModal>
  )
}

export default KiotListExpandDetailModal
